import { isValidCampaignSlug, useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { useLocale } from '@/components/Functional/hooks-core/useLocale'
import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import {
  useTanstackMenuFetchFn,
  useTanstackUserProfileFetchFn,
} from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { useLogout } from '@/components/Functional/hooks/useLogout'
import { authV2Url } from '@/components/Functional/lib/authUrls'
import { useStampPointLabelFromSheetTypes } from '@/components/Functional/useStampPointLabel'
import type { MenuData, SnsAction } from '@/schemas/v2'
import { useMenuStore } from '@/store/useMenuStore'
import { useTokenStore } from '@/store/useTokenStore'
import { Overlay, Portal, Transition } from '@mantine/core'
import { IconBrandFacebook, IconBrandInstagram, IconBrandLine, IconBrandTiktok, IconBrandX } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Fragment, type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './MenuDrawer.module.css'

/** http: または https: スキームのみを許可する */
const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * SNS action → アイコンコンポーネントのマップ。
 * Record<SnsAction, ...> により、SnsActionSchema に種別を追加した際に
 * コンパイルエラーで対応漏れを検知できる。
 */
const SNS_ICON_MAP: Record<SnsAction, ReactElement> = {
  facebook: <IconBrandFacebook size={32} stroke={1.5} />,
  instagram: <IconBrandInstagram size={32} stroke={1.5} />,
  x: <IconBrandX size={32} stroke={1.5} />,
  line: <IconBrandLine size={32} stroke={1.5} />,
  tiktok: <IconBrandTiktok size={32} stroke={1.5} />,
}

export function MenuDrawer() {
  const { t } = useTranslation()
  const { isOpen, close } = useMenuStore()
  const { gen, campaignSlug } = useCampaignSlug()
  const { locale, withLang } = useLocale()
  const { logout } = useLogout()
  const router = useRouter()

  // 認証状態を取得（tokenLoading === true の間は isAuthenticated = false として扱い、チラつきを防ぐ）
  const idToken = useTokenStore((s) => s.idToken)
  const tokenLoading = useTokenStore((s) => s.loading)
  const isAuthenticated = !tokenLoading && idToken !== null

  // campaignData.sheetTypes からスタンプ/ポイントラベルを導出
  const campaignData = AppMachineContext.useSelector((s) => s.context.campaignData)
  const campaignSheetTypes = campaignData?.sheetTypes ?? []
  const { label: stampPointLabel } = useStampPointLabelFromSheetTypes(campaignSheetTypes)

  const menuQuery = useTanstackMenuFetchFn()
  const { data: menuData } = useQuery({ ...menuQuery })

  const userProfileQuery = useTanstackUserProfileFetchFn()
  const { data: userProfile } = useQuery({ ...userProfileQuery, enabled: isAuthenticated })

  const handleLogout = async () => {
    await logout()
    router.reload()
  }

  /** メニュー項目を SNS とそれ以外に分離 */
  const { menuItems, snsItems } = useMemo(() => {
    const menus = menuData?.menus ?? []
    return {
      menuItems: menus.filter((item): item is Extract<MenuData, { type: 'URL' | 'STATIC' }> => item.type !== 'SNS'),
      snsItems: menus.filter((item): item is Extract<MenuData, { type: 'SNS' }> => item.type === 'SNS'),
    }
  }, [menuData])

  /** STATIC action に対応する URL を返す */
  const staticActionUrl = (action: string): string | null => {
    const docsUrl = process.env.NEXT_PUBLIC_DOCS_URL
    if (!docsUrl) return null

    // 利用規約・プライバシーはキャンペーンごとに内容が異なるため識別子付きパスを使う。
    // 識別子が無効（未取得・'[campaign]' 等）の場合は従来パスにフォールバックする。
    const docsBase = isValidCampaignSlug(campaignSlug) ? `${docsUrl}/${campaignSlug}` : docsUrl

    // Docs 画面のロケールは `lang` クエリで渡す（#3504）。
    // 管理画面から登録される type: 'URL' の外部リンクには付けない（他社サイトのため）
    switch (action) {
      case 'FAQ':
        return withLang(`${docsUrl}/faq`)
      case 'TERMS':
        return withLang(`${docsBase}/terms`)
      case 'PRIVACY_POLICY':
        return withLang(`${docsBase}/privacy`)
      case 'TOKUSHOHO':
        return withLang(`${docsBase}/tokushoho`)
      default:
        return null
    }
  }

  // ドロワーの背景は MenuDrawer.module.css の --main-color。文字色は #3529 で算出した on-color を使う
  const itemClass = (idx: number) => `block text-h1 font-bold text-on-main text-left ${idx >= 1 ? 'mt-5' : ''}`

  const renderMenuItem = (item: MenuData, idx: number) => {
    const cls = itemClass(idx)

    switch (item.type) {
      case 'URL':
        if (!item.url || !isSafeUrl(item.url)) {
          return null
        }
        return (
          <a href={item.url} className={cls} target="_blank" rel="noreferrer noopener">
            {item.name}
          </a>
        )

      case 'STATIC': {
        const url = staticActionUrl(item.action)
        if (url) {
          return (
            <a href={url} className={cls} rel="noreferrer noopener">
              {item.name}
            </a>
          )
        }
        // 商品一覧（#3351）: EC有効時にAPIが返す。認証不要のため認証ガードより前で内部遷移する。
        // 表示名は API が返す name（ロケール対応済み）をそのまま使う
        if (item.action === 'PRODUCT_LIST') {
          return (
            <Link href={{ pathname: gen('/products/tickets') }} className={cls}>
              {item.name}
            </Link>
          )
        }
        // RESET / RESTORE / ACCOUNT_PASSWORD / LOGOUT はログイン時のみ表示
        // NOTE: tokenLoading === true の間も isAuthenticated === false となるため、
        // 認証確定前に項目が一瞬表示されるちらつきは発生しない。現状この実装で許容する。
        if (!isAuthenticated) return null
        if (item.action === 'RESET') {
          return (
            <button
              type="button"
              onClick={() => {
                location.href = gen('/reset')
              }}
              className={cls}
            >
              {t('common.reset', '獲得{{what}}をリセットする', { what: stampPointLabel })}
            </button>
          )
        }
        if (item.action === 'RESTORE') {
          return (
            <Link href={{ pathname: gen('/restore') }} className={cls}>
              {item.name || t('common.restore', 'データ復元')}
            </Link>
          )
        }
        // パスワード変更（#3616）: auth の画面へ同じタブで遷移する。
        // 変更するとすべての端末でログアウトされるため、新しいタブで開くと元のタブに失効済みの SPA が残る。
        // 表示名は API が返す name（ロケール対応済み）をそのまま使う
        if (item.action === 'ACCOUNT_PASSWORD') {
          // 識別子が無効（未取得・'[campaign]' 等）のときは存在しないキャンペーンの URL になるため出さない
          if (!isValidCampaignSlug(campaignSlug)) return null
          return (
            <a href={authV2Url.accountPassword(campaignSlug, locale)} className={cls} rel="noreferrer noopener">
              {item.name}
            </a>
          )
        }
        if (item.action === 'LOGOUT') {
          return (
            <button type="button" className={cls} onClick={handleLogout}>
              {item.name || t('common.logout', 'ログアウト')}
            </button>
          )
        }
        return null
      }

      default:
        return null
    }
  }

  return (
    <Portal>
      {/* 背景オーバーレイ：ヘッダー直下から表示 */}
      <Transition mounted={isOpen} transition="fade" duration={200}>
        {(transitionStyles) => <Overlay onClick={close} zIndex={999} style={{ ...transitionStyles, top: '44px' }} />}
      </Transition>

      {/* ドロワー本体：左からスライドイン、ヘッダー直下から全画面 */}
      <Transition mounted={isOpen} transition="slide-right" duration={300} timingFunction="ease">
        {(transitionStyles) => (
          <div className={styles.drawer} style={transitionStyles} data-testid="menu-drawer">
            <div className={styles.contents}>
              {menuItems.map((item, idx) => (
                <Fragment key={`menu-${item.action ?? item.url ?? idx}`}>{renderMenuItem(item, idx)}</Fragment>
              ))}
            </div>

            {/* フッター：項目の末尾（項目が少ない場合は右下）に SNS → ユーザーID → バージョンを表示（#3503） */}
            <div className={styles.footer} data-testid="menu-drawer-footer">
              {/* SNS アイコン：横並び */}
              {snsItems.length > 0 && (
                <div className="flex items-center gap-3" data-testid="menu-drawer-sns">
                  {snsItems.map((item, idx) => {
                    const icon = SNS_ICON_MAP[item.action]
                    if (!isSafeUrl(item.url)) return null
                    return (
                      <a
                        key={`sns-${item.action ?? item.url ?? idx}`}
                        href={item.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-on-main"
                        aria-label={item.action}
                      >
                        {icon}
                      </a>
                    )
                  })}
                </div>
              )}

              {/* バージョン表示・ユーザーID */}
              <div className="flex flex-col items-end gap-1">
                {isAuthenticated && userProfile?.user.id != null && (
                  <p className="text-xs text-on-main">id: {userProfile.user.id}</p>
                )}
                <p className="text-xs text-on-main">v2.0.30</p>
              </div>
            </div>
          </div>
        )}
      </Transition>
    </Portal>
  )
}
